import UserGameState from "~/libs/constants/UserGameState";
import type UserGameDetail from "~/libs/interfaces/UserGameDetail";

interface UserGameStateDetail {
  isLoggedIn: boolean
  isUserInGame: boolean
  isGameStarted: boolean
  isGameInCountdown: boolean
  isGameDrawn: boolean
  isGameClosed: boolean
  canCheckWin: boolean
  gameRegisterDatetime?: Date
  hasUserCheckedWin: boolean
  isUserWinner: boolean
  hasWinner: boolean
  hasUserCorrectPsc: boolean
  hasDrawSignedUpForPrize: boolean
}

export const useUserGameStateHelper = () => {
  const getUserGameStateDetail = (userGameDetail?: UserGameDetail | null): UserGameStateDetail => {
    const now = new Date();
    const timeDiffMs = userGameDetail?.activeDraw?.timeDiffMs || 0
    now.setTime(now.getTime() - timeDiffMs)

    const userGameStateDetail: UserGameStateDetail = {
      isLoggedIn: false,
      isUserInGame: false,
      isGameStarted: false,
      isGameInCountdown: false,
      isGameDrawn: false,
      isGameClosed: false,
      canCheckWin: false,
      hasUserCheckedWin: false,
      isUserWinner: false,
      hasWinner: false,
      hasUserCorrectPsc: false,
      hasDrawSignedUpForPrize: false,
    }

    userGameStateDetail.isLoggedIn = !!(userGameDetail?.user && userGameDetail?.user?.confirmed && !userGameDetail?.user?.blocked)
    userGameStateDetail.isUserInGame = !!(userGameDetail?.user?.psc && userGameDetail?.user?.hasAgeConsent && userGameDetail?.gameUser)

    userGameStateDetail.isGameStarted = !!(userGameDetail?.activeDraw?.startDatetime && userGameDetail?.activeDraw?.startDatetime.getTime() <= now.getTime())
    userGameStateDetail.isGameInCountdown = !!(userGameDetail?.activeDraw?.countdownDatetime &&
      userGameDetail?.activeDraw?.countdownDatetime?.getTime() <= now.getTime() &&
      userGameDetail?.activeDraw?.drawDatetime &&
      userGameDetail?.activeDraw?.drawDatetime?.getTime() > now.getTime())
    userGameStateDetail.isGameDrawn = !!(userGameDetail?.activeDraw?.drawDatetime &&
      userGameDetail?.activeDraw?.drawDatetime?.getTime() <= now.getTime())
    userGameStateDetail.isGameClosed = !!(userGameDetail?.activeDraw?.closeDatetime &&
      userGameDetail?.activeDraw?.closeDatetime?.getTime() <= now.getTime())

    userGameStateDetail.canCheckWin = !!(userGameStateDetail.isGameDrawn &&
      userGameStateDetail.isUserInGame && userGameDetail?.gameUser?.subscriptionDatetime &&
      userGameDetail?.activeDraw?.drawDatetime &&
      userGameDetail?.gameUser?.subscriptionDatetime?.getTime() <= userGameDetail?.activeDraw?.drawDatetime?.getTime())

    userGameStateDetail.hasUserCheckedWin = !!(userGameDetail?.activeDraw?.hasMeCheckedWin)
    userGameStateDetail.isUserWinner = !!(userGameDetail?.activeDraw?.isMeWinner && userGameDetail?.activeDraw?.hasMeCorrectPsc)
    userGameStateDetail.hasWinner = !!(userGameDetail?.activeDraw?.hasWinner)
    userGameStateDetail.hasUserCorrectPsc = !!(userGameDetail?.activeDraw?.hasMeCorrectPsc)
    userGameStateDetail.hasDrawSignedUpForPrize = !!(userGameDetail?.activeDraw?.hasSignedUpForPrize)

    return userGameStateDetail
  }
  const getUserGameCountdown = (userGameDetail?: UserGameDetail | null): Date | undefined => {
    const {
      isGameStarted,
      isGameDrawn,
    } = getUserGameStateDetail(userGameDetail)

    const now = new Date()
    const drawDatetime = userGameDetail?.activeDraw?.drawDatetime
    const timeDiffMs = userGameDetail?.activeDraw?.timeDiffMs || 0

    if (isGameStarted && !isGameDrawn && drawDatetime) {
      const result = new Date()
      result.setTime(drawDatetime.getTime() - (now.getTime() - timeDiffMs))
      return result
    }

    return undefined
  }
  const getUserGameState = (userGameDetail?: UserGameDetail | null): UserGameState => {
    const {
      isLoggedIn,
      isUserInGame,
      isGameStarted,
      isGameInCountdown,
      isGameDrawn,
      isGameClosed,
      canCheckWin,
      hasUserCheckedWin,
      isUserWinner,
      hasWinner,
      hasUserCorrectPsc,
      hasDrawSignedUpForPrize,
    } = getUserGameStateDetail(userGameDetail)


    // console.log('getUserGameState', {
    //   isLoggedIn,
    //   isUserInGame,
    //   isGameStarted,
    //   isGameInCountdown,
    //   isGameDrawn,
    //   isGameClosed,
    //   canCheckWin,
    //   hasUserCheckedWin,
    //   isUserWinner,
    //   hasWinner,
    //   hasUserCorrectPsc,
    //   hasDrawSignedUpForPrize,
    // })

    if (!isGameStarted || isGameClosed) {
      // Game not started
      return UserGameState.NOGAME
    } else {
      // Game started
      if (isLoggedIn) {
        if (isUserInGame) {
          if (isGameInCountdown) {
            // User countdown - last minute...
            return UserGameState.USERLASTMINUTE
          }
          if (isGameDrawn) {
            // Game drawn, check
            if (!canCheckWin) {
              return UserGameState.FIRSTGAME
            }

            if (hasUserCheckedWin && isUserWinner && !hasDrawSignedUpForPrize) {
              return UserGameState.USERWIN
            }

            if (hasUserCheckedWin && isUserWinner && hasDrawSignedUpForPrize) {
              return UserGameState.USERCLAIMED
            }

            if (hasUserCheckedWin && !isUserWinner && hasUserCorrectPsc) {
              return UserGameState.USERALMOSTWIN
            }

            if (hasUserCheckedWin && !isUserWinner && !hasUserCorrectPsc) {
              return UserGameState.USERLOST
            }

            // User drawn
            return UserGameState.USERDRAW
          }

          // Logged-in user in game
          return UserGameState.USERINGAME
        }
        if (isGameInCountdown) {
          // Logged in, not in game, in countdown
          // uzivatel se prihlasil, ale nema PSC a hra je v posledni minute - tam potrebuju horni cast z LASTMINUTE/USERLASTMINUTE a pak tlacitka z USERLOGGED
          return UserGameState.USERLOGGEDLASTMINUTE
        }
        if (isGameDrawn) {
          // logged in, not in game, draw finished
          // uzivatel se prihlasil, nema PSC a hra je vylosovana - tam potrebuju horni cast z DRAW/USERDRAW a pak tlacitka z USERLOGGED
          return UserGameState.USERLOGGEDDRAW
        }
        // User logged
        return UserGameState.USERLOGGED
      } else {
        // Not logged-in user
        if (isGameInCountdown) {
          // Not logged-in user - countdown - last minute...
          return UserGameState.LASTMINUTE
        }

        if (isGameDrawn) {
          // Not logged-in user - Game drawn
          return UserGameState.DRAW
        }
      }
    }

    // Default - not logged in
    return UserGameState.DEFAULT
  }

  return {
    getUserGameState,
    getUserGameCountdown
  }
}


